import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './CustomizeViewDrawer.css';

/* ── FIELD_REGISTRY — single source of truth for every toggleable field ─────
 * Mirrors the sectionMap in ToolDetailScreen.jsx exactly. Adding a new field to
 * the detail screen? Add it here too so Bob can toggle it.
 *
 * locked: true        — field is always-on, toggle disabled in drawer
 * lifecycleOnly: true — panel/field only appears when mode === 'lifecycle'
 * disposalOnly: true  — panel/field only appears when mode === 'disposal'
 *                       (panels/fields with neither flag appear in both modes)
 * fields: null        — panel has no inner-field granularity (one bulk toggle)
 */
export const PANELS = [
  {
    key: 'tool_identification',
    label: 'Tool Identification',
    fields: [
      { key: 'inventory_status',   label: 'Inventory Status (Found / Added)', locked: true },
      { key: 'supplier_tool_id',   label: 'Supplier Tool ID', locked: true },
      { key: 'alt_tool_id',        label: 'Alt Tool ID' },
      { key: 'cspc',               label: 'CSPC' },
      { key: 'barcode',            label: 'Barcode' },
      { key: 'qr_code',            label: 'QR Code' },
      { key: 'tool_type',          label: 'Tool Type' },
      { key: 'oem',                label: 'OEM' },
      { key: 'material',           label: 'Material' },
      { key: 'quantity',           label: 'Quantity' },
      { key: 'cavities',           label: 'Cavities' },
      { key: 'cavity_number_start', label: 'Cavity # Start', lifecycleOnly: true },
      { key: 'cavity_number_end',   label: 'Cavity # End',   lifecycleOnly: true },
      { key: 'die_op',             label: 'Die / Op' },
      { key: 'tool_code',          label: 'Tool Code' },
      { key: 'platform',           label: 'Platform' },
      { key: 'list_number',        label: 'List Number' },
      { key: 'tool_description',   label: 'Tool Description' },
      { key: 'part_description',   label: 'Part Description' },
    ],
  },
  {
    key: 'location',
    label: 'Location',
    fields: [
      { key: 'site_location',      label: 'Site Location' },
      { key: 'detail_location',    label: 'Detail Location' },
      { key: 'row_nbr',            label: 'Row Nbr' },
      { key: 'stack_nbr',          label: 'Stack Nbr' },
      { key: 'rack_nbr',           label: 'Rack Nbr' },
      { key: 'shelf_nbr',          label: 'Shelf Nbr' },
      { key: 'skid_nbr',           label: 'Skid Nbr' },
    ],
  },
  { key: 'photos',       label: 'Photos',       fields: null },
  { key: 'part_numbers', label: 'Part Numbers', fields: null },
  { key: 'asset_tags',   label: 'Asset Tags',   fields: null },
  {
    key: 'physical',
    label: 'Physical',
    fields: [
      { key: 'length',           label: 'Length' },
      { key: 'width',            label: 'Width' },
      { key: 'height',           label: 'Height' },
      { key: 'estimated_weight', label: 'Estimated Weight' },
      { key: 'weight_unit',      label: 'Weight Unit' },
    ],
  },
  {
    key: 'supply_chain',
    label: 'Supply Chain',
    fields: [
      { key: 'tier_level',       label: 'Tier Level' },
      { key: 'tier_duns',        label: 'Tier DUNS' },
      { key: 'ship_to_location', label: 'Ship To Location' },
      { key: 'last_ship_date',   label: 'Last Ship Date' },
    ],
  },
  {
    key: 'flags',
    label: 'Flags',
    fields: [
      { key: 'capacity_flag',    label: 'Capacity Flag' },
      { key: 'npn_flag',         label: 'NPN Flag' },
    ],
  },
  {
    key: 'disposal_context',
    label: 'Disposal Context',
    fields: [
      { key: 'retained_reason',  label: 'Retained Reason' },
    ],
  },
  {
    key: 'notes',
    label: 'Notes',
    fields: [
      { key: 'inventory_comments', label: 'Inventory Comments' },
      { key: 'tmg_data_comments',  label: 'TMG Data Comments' },
    ],
  },
  {
    key: 'lifecycle_data',
    label: 'Lifecycle Data',
    lifecycleOnly: true,
    fields: [
      { key: 'current_condition_score', label: 'Condition Score' },
      { key: 'warranty_shots',          label: 'Warranty Pieces' },
      { key: 'warranty_shots_source',   label: 'Warranty Source' },
      { key: 'actual_shots_to_date',    label: 'Actual Pieces' },
      { key: 'weekly_capacity',         label: 'Weekly Capacity' },
      { key: 'lifecycle_detail',        label: 'Lifecycle Detail' },
      { key: 'assessment_notes',        label: 'Assessment Notes' },
    ],
  },
];

// Fast field→panel lookup (used by isFieldVisible)
const FIELD_TO_PANEL = {};
PANELS.forEach(p => {
  if (p.fields) p.fields.forEach(f => { FIELD_TO_PANEL[f.key] = p.key; });
});

const STORAGE_PREFIX = 'tmg_customize_view_';

function buildDefaults() {
  const panels = {};
  PANELS.forEach(p => {
    panels[p.key] = { visible: true, fields: {} };
    if (p.fields) {
      p.fields.forEach(f => {
        panels[p.key].fields[f.key] = { visible: true, pinned: false };
      });
    }
  });
  return panels;
}

// Merge stored state with fresh defaults so newly-added panels/fields appear
// visible for users who already had customizations saved.
function mergeWithDefaults(stored) {
  const merged = {};
  PANELS.forEach(p => {
    const storedPanel = stored[p.key] || {};
    const panel = {
      visible: storedPanel.visible !== false, // default true, only false if explicitly set
      fields: {},
    };
    if (p.fields) {
      p.fields.forEach(f => {
        const storedField = storedPanel.fields?.[f.key] || {};
        panel.fields[f.key] = {
          visible: storedField.visible !== false,
          pinned: !!storedField.pinned,
        };
      });
    }
    merged[p.key] = panel;
  });
  return merged;
}

function loadState(siteId) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + siteId);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    if (typeof stored !== 'object' || !stored) return null;
    return mergeWithDefaults(stored);
  } catch { /* ignore */ }
  return null;
}

function saveState(siteId, state) {
  try {
    localStorage.setItem(STORAGE_PREFIX + siteId, JSON.stringify(state));
  } catch { /* ignore */ }
}

function clearState(siteId) {
  try {
    localStorage.removeItem(STORAGE_PREFIX + siteId);
  } catch { /* ignore */ }
}

/* ── Hook: useCustomizeView ─────────────────────────────────────────────── */
export function useCustomizeView(siteId) {
  const [state, setState] = useState(() => loadState(siteId) || buildDefaults());

  useEffect(() => {
    setState(loadState(siteId) || buildDefaults());
  }, [siteId]);

  useEffect(() => {
    if (siteId) saveState(siteId, state);
  }, [siteId, state]);

  // Internal: recompute a panel's `visible` flag based on whether ANY field is visible.
  // Panels with no fields keep their stored visible flag (they're toggled panel-level only).
  function reconcilePanelVisibility(panelState, panelDef) {
    if (!panelDef.fields) return panelState; // no-op for leaf panels
    const anyVisible = Object.values(panelState.fields).some(f => f.visible);
    return { ...panelState, visible: anyVisible };
  }

  const togglePanel = useCallback((panelKey) => {
    setState(prev => {
      const next = { ...prev };
      const panelDef = PANELS.find(p => p.key === panelKey);
      if (!panelDef) return prev;
      // For panels with fields, delegate to bulk-toggle all fields
      if (panelDef.fields) {
        const newVisible = !prev[panelKey].visible;
        const fields = {};
        Object.keys(prev[panelKey].fields).forEach(fk => {
          fields[fk] = { ...prev[panelKey].fields[fk], visible: newVisible };
        });
        next[panelKey] = { visible: newVisible, fields };
      } else {
        next[panelKey] = { ...prev[panelKey], visible: !prev[panelKey].visible };
      }
      return next;
    });
  }, []);

  const toggleFieldVisible = useCallback((panelKey, fieldKey) => {
    setState(prev => {
      const panelDef = PANELS.find(p => p.key === panelKey);
      if (!panelDef) return prev;
      const panel = { ...prev[panelKey], fields: { ...prev[panelKey].fields } };
      const current = panel.fields[fieldKey] || { visible: true, pinned: false };
      panel.fields[fieldKey] = { ...current, visible: !current.visible };
      const reconciled = reconcilePanelVisibility(panel, panelDef);
      return { ...prev, [panelKey]: reconciled };
    });
  }, []);

  const toggleFieldPinned = useCallback((panelKey, fieldKey) => {
    setState(prev => {
      const panel = { ...prev[panelKey], fields: { ...prev[panelKey].fields } };
      const current = panel.fields[fieldKey] || { visible: true, pinned: false };
      panel.fields[fieldKey] = { ...current, pinned: !current.pinned };
      return { ...prev, [panelKey]: panel };
    });
  }, []);

  // Bulk set every (unlocked) field in a panel to show or hide.
  const toggleAllPanelFields = useCallback((panelKey, show) => {
    setState(prev => {
      const panelDef = PANELS.find(p => p.key === panelKey);
      if (!panelDef) return prev;
      if (!panelDef.fields) {
        // No inner fields — just toggle the panel flag
        return { ...prev, [panelKey]: { ...prev[panelKey], visible: !!show } };
      }
      const fields = {};
      panelDef.fields.forEach(f => {
        const existing = prev[panelKey].fields[f.key] || { visible: true, pinned: false };
        // Locked fields stay visible no matter what
        fields[f.key] = { ...existing, visible: f.locked ? true : !!show };
      });
      const anyVisible = Object.values(fields).some(v => v.visible);
      return { ...prev, [panelKey]: { visible: anyVisible, fields } };
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    clearState(siteId);
    setState(buildDefaults());
  }, [siteId]);

  /* ── Derived selectors ──────────────────────────────────────────────── */

  // True when the named field is visible (or when nothing is recorded yet —
  // new / unknown fields default to visible so missing state never hides UI).
  const isFieldVisible = useCallback((fieldKey) => {
    const panelKey = FIELD_TO_PANEL[fieldKey];
    if (!panelKey) return true;
    const panel = state[panelKey];
    if (!panel) return true;
    const field = panel.fields?.[fieldKey];
    if (!field) return true;
    return field.visible !== false;
  }, [state]);

  // Panels the ToolDetailScreen should NOT render (Set<string>).
  const hiddenPanels = new Set(
    Object.entries(state)
      .filter(([, v]) => v && v.visible === false)
      .map(([k]) => k)
  );

  // Pinned fields flat list (used by ToolListScreen to render row extras).
  const pinnedFields = [];
  PANELS.forEach(p => {
    if (!p.fields || !state[p.key]) return;
    p.fields.forEach(f => {
      if (state[p.key].fields?.[f.key]?.pinned) {
        pinnedFields.push({ panelKey: p.key, fieldKey: f.key, label: f.label });
      }
    });
  });

  return {
    state, hiddenPanels, pinnedFields,
    togglePanel, toggleFieldVisible, toggleFieldPinned,
    toggleAllPanelFields, isFieldVisible,
    resetToDefaults,
  };
}

/* ── Drawer Component ───────────────────────────────────────────────────── */
export default function CustomizeViewDrawer({ open, onClose, mode, customizeView }) {
  // Panel rows are expanded by default so every toggle is immediately visible.
  // User can collapse a panel header to shorten the scroll — it's only a
  // navigation convenience, never a data-loss path.
  const [collapsedPanels, setCollapsedPanels] = useState(new Set());

  if (!open) return null;

  const {
    state, togglePanel, toggleFieldVisible, toggleFieldPinned,
    toggleAllPanelFields, resetToDefaults,
  } = customizeView;

  // Mode-scoped filter — panels/fields may be tagged `lifecycleOnly` or
  // `disposalOnly` to hide them in the opposite mode. No tag = shown in both.
  const visiblePanels = PANELS.filter(p => {
    if (p.lifecycleOnly && mode !== 'lifecycle') return false;
    if (p.disposalOnly && mode !== 'disposal') return false;
    return true;
  });

  function toggleCollapsed(panelKey) {
    setCollapsedPanels(prev => {
      const next = new Set(prev);
      if (next.has(panelKey)) next.delete(panelKey); else next.add(panelKey);
      return next;
    });
  }

  const drawer = (
    <>
      <div className="cvd-overlay" onClick={onClose} />
      <div className="cvd-sheet">
        <div className="cvd-handle" />
        <div className="cvd-header">
          <h2 className="cvd-title">Customize View</h2>
          <button className="cvd-done" onClick={onClose}>Done</button>
        </div>
        <div className="cvd-body">
          {visiblePanels.map(panel => {
            const ps = state[panel.key] || { visible: true, fields: {} };
            const isCollapsed = collapsedPanels.has(panel.key);
            const hasFields = Array.isArray(panel.fields) && panel.fields.length > 0;
            const panelFields = hasFields
              ? panel.fields.filter(f => {
                  if (f.lifecycleOnly && mode !== 'lifecycle') return false;
                  if (f.disposalOnly && mode !== 'disposal') return false;
                  return true;
                })
              : [];

            return (
              <div key={panel.key} className="cvd-panel">
                <div className="cvd-panel-row">
                  <button
                    className="cvd-panel-left"
                    onClick={() => hasFields && toggleCollapsed(panel.key)}
                    aria-label={hasFields ? `Collapse ${panel.label}` : panel.label}
                    type="button"
                  >
                    <span className="cvd-panel-label">{panel.label}</span>
                    {hasFields && (
                      <span className={`cvd-panel-chevron ${isCollapsed ? '' : 'open'}`}>
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                          <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    )}
                  </button>
                  <button
                    className={`cvd-toggle ${ps.visible ? 'on' : 'off'}`}
                    onClick={() => {
                      if (hasFields) toggleAllPanelFields(panel.key, !ps.visible);
                      else togglePanel(panel.key);
                    }}
                    aria-label={`Toggle ${panel.label}`}
                  >
                    <span className="cvd-toggle-knob" />
                  </button>
                </div>

                {hasFields && !isCollapsed && (
                  <div className="cvd-fields">
                    {panelFields.map(field => {
                      const fs = ps.fields?.[field.key] || { visible: true, pinned: false };
                      return (
                        <div key={field.key} className="cvd-field-row">
                          <span className={`cvd-field-label ${field.locked ? 'locked' : ''}`}>
                            {field.label}
                          </span>
                          <div className="cvd-field-controls">
                            {!field.locked && (
                              <button
                                className={`cvd-pin ${fs.pinned ? 'active' : ''}`}
                                onClick={() => toggleFieldPinned(panel.key, field.key)}
                                aria-label={`Pin ${field.label}`}
                                title="Show in list view"
                                type="button"
                              >
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                  <path d="M9.5 1.5L14.5 6.5L11 10L12 14L2 12L6 10L1.5 5.5L9.5 1.5Z"
                                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                                    fill={fs.pinned ? 'currentColor' : 'none'} />
                                </svg>
                              </button>
                            )}
                            {field.locked ? (
                              <span className="cvd-always-on">Always on</span>
                            ) : (
                              <button
                                className={`cvd-toggle sm ${fs.visible ? 'on' : 'off'}`}
                                onClick={() => toggleFieldVisible(panel.key, field.key)}
                                aria-label={`Toggle ${field.label}`}
                                type="button"
                              >
                                <span className="cvd-toggle-knob" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <button className="cvd-reset" onClick={() => { resetToDefaults(); setCollapsedPanels(new Set()); }} type="button">
            Reset to defaults
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(drawer, document.body);
}
